if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 1024px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        // Task 7e: choose the first real source width that covers CSS pixels × DPR.
        this.imageCandidates = new WeakMap();
        this.observedImages = new Set();
        this.imageResize = new ResizeObserver(() => this.refreshImageSizes());
        this.imageMutations = new MutationObserver(() => this.refreshImageSizes());
        this.imageRoot = this.closest('product-info') || this;
        this.imageMutations.observe(this.imageRoot, { childList: true, subtree: true });
        this.onImageResize = () => this.refreshImageSizes();
        window.addEventListener('resize', this.onImageResize);
        this.refreshImageSizes();
        if (this.dataset.mcRoomScript && !customElements.get('mc-room-guide')) this.deferRoomGuide();
        this.infographicUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== String(this.closest('product-info')?.sectionId)) return;
          const slides = [...this.querySelectorAll('.product__media-list > [data-media-id]')];
          const offsets = slides.map(item => item.offsetLeft);
          const modal = this.closest('product-info')?.productModal;
          const infographicHidden = new Map();
          this.querySelectorAll('.product__media-list > [data-mc-infographic]').forEach(item => {
            const source = data.html.querySelector(`[data-mc-infographic][data-media-id="${item.dataset.mediaId}"]`);
            infographicHidden.set(item.dataset.mediaId, !source || source.hidden);
          });
          this.fillDeferredInfographics(data.html, infographicHidden, modal);
          this.querySelectorAll('[data-mc-infographic]').forEach(item => {
            item.hidden = infographicHidden.get(item.dataset.mediaId || item.dataset.target) ?? true;
          });
          // The visibility change moves later slides in the horizontal track.
          // Compensate that move with a transform, so it is not a layout shift.
          slides.forEach((item, index) => {
            const delta = offsets[index] - item.offsetLeft;
            if (delta && !item.hidden) item.animate(
              [{ transform: 'translateX(' + delta + 'px)' }, { transform: 'translateX(0)' }],
              { duration: matchMedia('(prefers-reduced-motion: reduce)').matches ? 1 : 250, easing: 'ease-out' }
            );
          });
          // Keep native thumbnail announcements aligned when the extra slide appears/disappears.
          this.querySelectorAll('[data-target]').forEach(item => {
            const target = data.html.querySelector(`[data-target="${item.dataset.target}"]`);
            if (!target) return;
            item.dataset.mediaPosition = target.dataset.mediaPosition;
            item.querySelector('button').setAttribute('aria-label', target.querySelector('button').getAttribute('aria-label'));
          });
          modal?.querySelectorAll('[data-mc-infographic]').forEach(item => {
            item.hidden = infographicHidden.get(this.closest('product-info').sectionId + '-' + item.dataset.mediaId) ?? true;
          });
          if (infographicHidden.get(this.dataset.currentMedia)) {
            this.setActiveMedia(this.querySelector('.product__media-list > [data-media-id]:not([hidden])').dataset.mediaId, false);
          }
          if (modal && infographicHidden.get(this.closest('product-info').sectionId + '-' + modal.activeId)) {
            modal.activeId = modal.mediaOrder()[0];
            if (modal.hasAttribute('open')) modal.showActiveMedia();
          }
          // Hiding a middle slide changes offsets. Keep the selected slide in place
          // in this same frame, before the native deferred scroll can animate.
          const active = this.elements.viewer.querySelector('.product__media-list > .is-active:not([hidden])');
          if (active && !this.mql.matches) {
            active.parentElement.scrollTo({ left: active.offsetLeft, behavior: 'instant' });
          }
          this.elements.viewer.resetPages?.();
          this.elements.thumbnails?.resetPages?.();
        });
      }

      disconnectedCallback() {
        this.roomAbort?.abort();
        this.roomObservers?.forEach((observer) => observer.disconnect());
        this.infographicUnsubscribe?.();
        this.imageResize?.disconnect();
        this.imageMutations?.disconnect();
        window.removeEventListener('resize', this.onImageResize);
      }

      // Plan 13 B1: hidden infographics are rendered as empty shells (data-mc-deferred).
      // The section response of the variant change carries the full markup.
      fillDeferredInfographics(html, hidden, modal) {
        const sectionId = this.closest('product-info')?.sectionId;
        this.querySelectorAll('[data-mc-infographic][data-mc-deferred]').forEach((item) => {
          const id = item.dataset.mediaId || item.dataset.target;
          if (hidden.get(id) !== false) return;
          if (item.dataset.mediaId) {
            const source = html.querySelector(
              `.product__media-list > [data-mc-infographic][data-media-id="${id}"]:not([data-mc-deferred])`
            );
            if (!source) return;
            item.innerHTML = source.innerHTML;
          } else {
            // Keep the button: the thumbnail click listener is bound to it.
            const button = item.querySelector('button');
            const source = html.querySelector(`[data-mc-infographic][data-target="${id}"]:not([data-mc-deferred]) button`);
            if (!button || !source) return;
            button.innerHTML = source.innerHTML;
          }
          item.removeAttribute('data-mc-deferred');
          this.runInlineScripts(item);
        });
        // Variants without featured_media do not replace the modal content in product-info.js.
        modal?.querySelectorAll('[data-mc-infographic][data-mc-deferred]').forEach((item) => {
          if (hidden.get(sectionId + '-' + item.dataset.mediaId) !== false) return;
          const source = html.querySelector(
            `product-modal [data-mc-infographic][data-media-id="${item.dataset.mediaId}"]:not([data-mc-deferred])`
          );
          if (!source) return;
          const copy = document.importNode(source, true);
          item.replaceWith(copy);
          this.runInlineScripts(copy);
        });
      }

      // Scripts inserted via innerHTML/importNode do not run (mcMk scales the mockup layers).
      runInlineScripts(root) {
        root.querySelectorAll('script').forEach((old) => {
          const script = document.createElement('script');
          [...old.attributes].forEach(({ name, value }) => script.setAttribute(name, value));
          script.textContent = old.textContent;
          old.replaceWith(script);
        });
      }

      // Plan 13 C2: mc-room-guide.js loads on the first gallery interaction, when the
      // room slide is next to the visible one, or when the room thumbnail is visible
      // (only after load + idle, so it does not compete with the LCP image).
      deferRoomGuide() {
        this.roomAbort?.abort();
        this.roomObservers?.forEach((observer) => observer.disconnect());
        const abort = new AbortController();
        const observers = [];
        this.roomAbort = abort;
        this.roomObservers = observers;
        const load = () => {
          if (abort.signal.aborted) return;
          abort.abort();
          observers.forEach((observer) => observer.disconnect());
          window.mcLoadScript(this.dataset.mcRoomScript).catch((error) => console.error(error));
        };
        const observe = (target, options) => {
          const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) load();
          }, options);
          observer.observe(target);
          observers.push(observer);
        };
        const options = { signal: abort.signal, passive: true };
        ['pointerdown', 'touchstart', 'keydown', 'wheel'].forEach((type) => this.addEventListener(type, load, options));
        this.elements.viewer?.addEventListener('slideChanged', load, options);
        const list = this.querySelector('.product__media-list');
        list?.addEventListener('scroll', load, options);
        const slide = list?.querySelector(':scope > .mc-room-slide');
        if (slide) observe(slide, { root: list, rootMargin: '0px 100%' });
        const thumbnail = this.querySelector('.mc-room-thumbnail');
        if (!thumbnail) return;
        const observeThumbnail = () => {
          if (!abort.signal.aborted) observe(thumbnail);
        };
        const idle = () => {
          if (window.requestIdleCallback) requestIdleCallback(observeThumbnail, { timeout: 2000 });
          else setTimeout(observeThumbnail, 2000);
        };
        if (document.readyState === 'complete') idle();
        else window.addEventListener('load', idle, { once: true, signal: abort.signal });
      }

      refreshImageSizes() {
        if (!this.imageCandidates) return;
        const images = [...this.querySelectorAll('img'), ...this.imageRoot.querySelectorAll('.mc-switcher__thumb img')];
        for (const image of this.observedImages) {
          if (!images.includes(image)) { this.imageResize.unobserve(image); this.observedImages.delete(image); }
        }
        images.forEach(image => {
          if (!this.observedImages.has(image)) { this.observedImages.add(image); this.imageResize.observe(image); }
          if (image.dataset.roomResponsive === 'true') return;
          if (!image.srcset) return;
          let candidates = this.imageCandidates.get(image);
          if (!candidates) {
            candidates = image.srcset.split(',').map(item => {
              const match = item.trim().match(/^(\S+)\s+(\d+)w$/);
              return match ? { url: match[1], width: Number(match[2]) } : null;
            }).filter(Boolean).sort((a, b) => a.width - b.width);
            if (!candidates.length) return;
            this.imageCandidates.set(image, candidates);
          }
          const width = image.getBoundingClientRect().width;
          if (width <= 0) return;
          const needed = Math.ceil(width * (window.devicePixelRatio || 1));
          const adequate = candidates.filter(candidate => candidate.width >= needed);
          // Source candidates are capped by Liquid/the decoded source. Never invent upscaled widths.
          const selected = adequate.length ? adequate : candidates.slice(-1);
          const srcset = selected.map(candidate => candidate.url + ' ' + candidate.width + 'w').join(', ');
          const sizes = Number(width.toFixed(3)) + 'px';
          if (image.sizes !== sizes) image.sizes = sizes;
          if (image.srcset !== srcset) image.srcset = srcset;
          image.dataset.resolutionLimited = String(!adequate.length);
        });
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]').forEach(item => {
          item.classList.toggle('is-active', item === event.detail.currentElement);
        });
        this.dataset.currentMedia = event.detail.currentElement.dataset.mediaId;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        // Task 7: variant updates keep the room selected and all media in order.
        if (prepend && this.hasAttribute('data-room-guide-gallery') &&
            this.dataset.currentMedia?.endsWith('-room-guide')) return;
        this.dataset.currentMedia = mediaId;
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend && !this.hasAttribute('data-room-guide-gallery')) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
